// Inspecteur → Bedrijfsmiddelen (business/IT-asset register) — FRONTEND-BLUEPRINT §3.1 / §4. Re-skin on the
// shadcn design system: PageHeader + DataTable, reusing api.listBedrijfsmiddelen()/createBedrijfsmiddel()
// (no new endpoints). Columns: category · class · name · description.
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { TENANT_PATHS } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import type { Column } from "@/components/data-table";
import {
  RegisterScreen, CreateDialog, EditDialog, DeactivateButton, TagBadge, truncate, type Row, type FormField,
} from "./_shared";
import { ASSET_CATEGORIES, ASSET_CLASSES, VULNERABILITY_6, LIKELIHOOD } from "../../lib/taxonomy";

export function InspectorAssetsRoute() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["bedrijfsmiddelen"] });
  const [editing, setEditing] = useState<string | null>(null);

  // L5 + Phase C catalog dropdowns; labels via i18n (P3). Built in-component so t() is in scope.
  const FIELDS: FormField[] = [
    { key: "name", label: t("ins.assets.field.name"), required: true },
    { key: "category", label: t("ins.assets.field.category"), type: "select", options: ASSET_CATEGORIES },
    { key: "assetClass", label: t("ins.assets.field.class"), type: "select", options: ASSET_CLASSES },
    { key: "vulnerability", label: t("ins.assets.field.vulnerability"), type: "select", options: VULNERABILITY_6 },
    { key: "likelihood", label: t("ins.assets.field.likelihood"), type: "select", options: LIKELIHOOD },
    { key: "description", label: t("ins.assets.field.description"), type: "textarea" },
  ];

  const columns: Column<Row>[] = [
    { key: "category", header: t("ins.assets.col.category"), render: (r) => <TagBadge value={r.category} /> },
    { key: "assetClass", header: t("ins.assets.col.class"), render: (r) => <TagBadge value={r.assetClass} /> },
    { key: "name", header: t("ins.assets.col.name"), render: (r) => <span className="font-medium">{String(r.name ?? "—")}</span> },
    { key: "vulnerability", header: t("ins.assets.col.vulnerability"), render: (r) => truncate(r.vulnerability) },
    { key: "likelihood", header: t("ins.assets.col.likelihood"), render: (r) => <TagBadge value={r.likelihood} /> },
  ];

  return (
    <>
      <RegisterScreen
        title={t("nav.assets")}
        description={t("inspector.assets.sub")}
        help={t("help.assets")}
        queryKey="bedrijfsmiddelen"
        listFn={() => api.listBedrijfsmiddelen()}
        columns={columns}
        empty={t("inspector.assets.empty")}
        readPerm="bedrijfsmiddel.read"
        onRowClick={(r) => { if (r.id) void navigate({ to: "/bedrijfsmiddelen/$id", params: { id: String(r.id) } }); }}
        actions={
          <Can perm="bedrijfsmiddel.write">
            <CreateDialog
              title={t("inspector.assets.new")}
              fields={FIELDS}
              onCreate={(i) => api.createBedrijfsmiddel(i as unknown as Parameters<typeof api.createBedrijfsmiddel>[0])}
              onDone={invalidate}
            />
          </Can>
        }
        rowActions={(r) => {
          const id = String(r.id);
          return (
            <span className="inline-flex items-center gap-1">
              <Can perm="bedrijfsmiddel.update"><button type="button" className="text-xs text-primary hover:underline" onClick={() => setEditing(id)}>{t("crud.edit")}</button></Can>
              <Can perm="bedrijfsmiddel.delete"><DeactivateButton basePath={TENANT_PATHS.bedrijfsmiddelen} id={id} onDone={invalidate} /></Can>
            </span>
          );
        }}
      />
      {editing && (
        <EditDialog
          basePath={TENANT_PATHS.bedrijfsmiddelen}
          id={editing}
          title={t("crud.edit")}
          fields={FIELDS}
          onClose={() => setEditing(null)}
          onDone={() => { invalidate(); setEditing(null); }}
        />
      )}
    </>
  );
}
